import {
  SIDEBAR_CATEGORIES,
  type SidebarCategoryId,
  workflowBuilderRegistryFields,
  type WorkflowRegistryFields,
} from './Sidebar/sidebarPalette';
import type { FlowLlmNodePersistedData } from './llmWorkflowTypes';
import { buildLlmOutputLines } from './llmWorkflowTypes';
import type { FlowAgentPersistedData } from './agentWorkflowTypes';
import { buildAgentOutputLines } from './agentWorkflowTypes';
import type { TriggerNodeData } from './TriggerNode/TriggerNode';
import type { TriggerSettings } from './triggerWorkflowTypes';
import type { NodeData } from './CommonNode/CommonNode';
import { slugifyAgentNodeName } from './agentWorkflowTypes';
import type { Node } from 'reactflow';
import type { FlowFxPersistedData } from './flowFxWorkflowTypes';
import { flowFxOutputHints } from './flowFxWorkflowTypes';
import { SEND_EMAIL_SAMPLE_TEMPLATES } from './sendEmailTemplates';

function isFlowTargetHandle(handle: string | null | undefined): boolean {
  if (handle == null || handle === 'in') return true;
  return /^in-\d+$/.test(handle);
}

/** Internal action discriminator (spec action types). */
export type ActionKindId =
  | 'send-email'
  | 'send-slack'
  | 'http-request'
  | 'update-crm'
  | 'emit-event'
  | 'create-record'
  | 'delete-record'
  | 'logger'
  | 'wait'
  | 'condition-check'
  | 'html-viewer';

export type FlowActionPersistedData = {
  label: string;
  paletteType: string;
  actionKind: ActionKindId;
  nodeName: string;
  /** Filled on canvas for display hints */
  incomingInputHint?: string;

  emailToLines: string[];
  emailCcLines: string[];
  emailBccLines: string[];
  emailSubject: string;
  emailBody: string;
  emailBodyFormat: 'plain' | 'html';
  emailAttachments: string[];
  emailAdvancedOpen: boolean;
  emailReplyTo: string;
  /** Last-applied built-in sample template id; empty/omitted = custom. */
  emailTemplateId?: string;

  slackChannel: string;
  slackMessage: string;
  slackFormat: 'markdown' | 'plain';
  slackMention: string;
  slackBlocksOpen: boolean;

  httpMethod: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  httpUrl: string;
  httpHeaders: { id: string; key: string; value: string }[];
  httpBody: string;
  httpBodyFormat: 'json' | 'text';
  httpTimeoutMs: number;
  httpRetry: boolean;
  httpRetryCount: number;

  crmEntity: string;
  crmRecordId: string;
  crmFields: { id: string; key: string; value: string }[];
  crmCreateIfMissing: boolean;
  crmTriggerAutomations: boolean;

  emitEventName: string;
  emitPayload: string;

  createEntity: string;
  createFields: { id: string; key: string; value: string }[];

  deleteEntity: string;
  deleteRecordId: string;

  logLevel: 'debug' | 'info' | 'warn' | 'error';
  logMessage: string;

  waitDelayType: 'duration' | 'until' | 'condition';
  waitDuration: string;
  waitUnit: 'seconds' | 'minutes' | 'hours' | 'days';
  waitUntilTime: string;
  waitUntilTz: string;
  waitUntilDateLabel: string;
  waitCondition: string;
  waitConditionTimeoutHours: string;

  conditionExpr: string;

  /** HTML fragment or full document; execution treats this as text/html only (no Markdown/plain). */
  htmlViewerHtml: string;
} & WorkflowRegistryFields;

export type ActionValidationLevel = 'valid' | 'warning' | 'error';

export type ActionValidation = {
  level: ActionValidationLevel;
  missingRequired: string[];
  missingOptional: string[];
  varMappedCount: number;
};

const VAR_RE = /\{\{\s*[^}]+\s*\}\}/g;

function countVarsInStrings(parts: string[]): number {
  const set = new Set<string>();
  for (const s of parts) {
    const m = s.match(VAR_RE);
    if (!m) continue;
    for (const x of m) set.add(x.trim());
  }
  return set.size;
}

function newRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isActionsCategory(category: SidebarCategoryId): boolean {
  return category === SIDEBAR_CATEGORIES.ACTIONS;
}

export function paletteTypeToActionKind(paletteType: string): ActionKindId | null {
  const map: Record<string, ActionKindId> = {
    Action_SendEmail: 'send-email',
    Action_SendSlack: 'send-slack',
    Action_HttpRequest: 'http-request',
    Action_UpdateRecord: 'update-crm',
    Action_WaitDelay: 'wait',
    Action_EmitEvent: 'emit-event',
    Action_CreateRecord: 'create-record',
    Action_DeleteRecord: 'delete-record',
    Action_Logger: 'logger',
    Action_ConditionCheck: 'condition-check',
    Action_HtmlViewer: 'html-viewer',
  };
  return map[paletteType] ?? null;
}

export function actionBadgeLabel(kind: ActionKindId, data: FlowActionPersistedData): string {
  switch (kind) {
    case 'send-email':
      return 'SMTP';
    case 'send-slack':
      return 'POST';
    case 'http-request':
      return data.httpMethod;
    case 'update-crm':
      return 'PATCH';
    case 'emit-event':
      return 'EMIT';
    case 'create-record':
      return 'INSERT';
    case 'delete-record':
      return 'DELETE';
    case 'logger':
      return 'LOG';
    case 'wait':
      return 'DELAY';
    case 'condition-check':
      return 'IF';
    case 'html-viewer':
      return 'HTML';
    default:
      return 'ACT';
  }
}

export function slugifyActionNodeName(raw: string): string {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return s || 'action';
}

function emptyActionCore(
  label: string,
  paletteType: string,
  actionKind: ActionKindId,
  nodeName: string
): FlowActionPersistedData {
  return {
    label,
    paletteType,
    actionKind,
    nodeName,
    emailToLines: [''],
    emailCcLines: [''],
    emailBccLines: [''],
    emailSubject: '',
    emailBody: '',
    emailBodyFormat: 'plain',
    emailAttachments: [],
    emailAdvancedOpen: false,
    emailReplyTo: '',
    emailTemplateId: '',
    slackChannel: '',
    slackMessage: '',
    slackFormat: 'markdown',
    slackMention: '',
    slackBlocksOpen: false,
    httpMethod: 'POST',
    httpUrl: '',
    httpHeaders: [],
    httpBody: '',
    httpBodyFormat: 'json',
    httpTimeoutMs: 5000,
    httpRetry: false,
    httpRetryCount: 3,
    crmEntity: 'Contact',
    crmRecordId: '',
    crmFields: [{ id: newRowId(), key: 'status', value: '' }],
    crmCreateIfMissing: false,
    crmTriggerAutomations: false,
    emitEventName: '',
    emitPayload: '{\n  \n}',
    createEntity: 'Contact',
    createFields: [{ id: newRowId(), key: '', value: '' }],
    deleteEntity: 'Contact',
    deleteRecordId: '',
    logLevel: 'info',
    logMessage: '',
    waitDelayType: 'duration',
    waitDuration: '30',
    waitUnit: 'minutes',
    waitUntilTime: '09:00',
    waitUntilTz: 'UTC',
    waitUntilDateLabel: 'Next weekday',
    waitCondition: '',
    waitConditionTimeoutHours: '24',
    conditionExpr: '',
    htmlViewerHtml: '',
  };
}

export function createDefaultActionData(
  payload: { type: string; label: string } & WorkflowRegistryFields
): FlowActionPersistedData | null {
  const kind = paletteTypeToActionKind(payload.type);
  if (!kind) return null;
  const nodeName = slugifyActionNodeName(payload.label);
  const base = emptyActionCore(payload.label, payload.type, kind, nodeName);

  switch (kind) {
    case 'send-email': {
      base.emailToLines = ['{{ lead.email }}'];
      const first = SEND_EMAIL_SAMPLE_TEMPLATES[0];
      base.emailTemplateId = first?.id ?? '';
      base.emailSubject = first?.subject ?? 'Your report is ready';
      base.emailBody =
        first?.body ?? 'Hi {{ lead.name }},\n\nYour analysis is ready.\n\nBest regards';
      base.emailBodyFormat = first?.bodyFormat ?? 'plain';
      break;
    }
    case 'send-slack':
      base.slackChannel = '#sales-alerts';
      base.slackMessage = '{{ agent.summary }}';
      break;
    case 'http-request':
      base.httpMethod = 'POST';
      base.httpUrl = 'https://api.example.com';
      base.httpBody = '{\n  "leadId": "{{ lead.id }}"\n}';
      break;
    case 'update-crm':
      base.crmRecordId = '{{ lead.id }}';
      base.crmFields = [
        { id: newRowId(), key: 'status', value: '"qualified"' },
        { id: newRowId(), key: 'score', value: '{{ score }}' },
      ];
      break;
    case 'emit-event':
      base.emitEventName = 'lead.qualified';
      base.emitPayload = `{\n  "leadId": "{{ lead.id }}",\n  "score": {{ score }}\n}`;
      break;
    case 'create-record':
      base.createFields = [{ id: newRowId(), key: 'name', value: '{{ lead.name }}' }];
      break;
    case 'delete-record':
      base.deleteRecordId = '{{ lead.id }}';
      break;
    case 'logger':
      base.logMessage = '{{ workflow.id }} — step complete';
      break;
    case 'wait':
      base.waitDuration = '30';
      base.waitUnit = 'minutes';
      break;
    case 'condition-check':
      base.conditionExpr = '{{ status }} == "approved"';
      break;
    case 'html-viewer':
      base.htmlViewerHtml = '<div>\n  {{ previous.output }}\n</div>';
      break;
    default:
      break;
  }

  return {
    ...base,
    ...workflowBuilderRegistryFields({
      category: payload.workflowCategoryid,
      controlUuid: payload.workflowControlid,
      categoryUuid: payload.workflowCategoryuuid,
    }),
  };
}

export function buildActionPreviewRows(kind: ActionKindId, d: FlowActionPersistedData): { k: string; v: string }[] {
  const trunc = (s: string, n = 42) => {
    const t = s.replace(/\s+/g, ' ').trim();
    if (t.length <= n) return t;
    return `${t.slice(0, n - 1)}…`;
  };

  switch (kind) {
    case 'send-email':
      return [
        { k: 'To', v: trunc(d.emailToLines.filter(Boolean).join(', ') || '—') },
        { k: 'Subj', v: trunc(d.emailSubject || '—') },
      ];
    case 'send-slack':
      return [
        { k: 'Ch', v: trunc(d.slackChannel || '—') },
        { k: 'Msg', v: trunc(d.slackMessage || '—', 48) },
      ];
    case 'http-request':
      return [
        { k: `${d.httpMethod}`, v: trunc(d.httpUrl || '—') },
        { k: 'Body', v: trunc(d.httpBody || '—', 40) },
      ];
    case 'update-crm':
      return [
        { k: `${d.crmEntity}`, v: trunc(`→ ${d.crmRecordId || '—'}`) },
        {
          k: 'Set',
          v: trunc(
            d.crmFields
              .filter((f) => f.key.trim())
              .map((f) => `${f.key}=${f.value}`)
              .join(', ') || '—'
          ),
        },
      ];
    case 'emit-event':
      return [
        { k: '⚡', v: trunc(d.emitEventName || '—') },
        { k: 'Payload', v: trunc(d.emitPayload.replace(/\s+/g, ' '), 44) },
      ];
    case 'create-record':
      return [
        { k: 'Ent', v: d.createEntity || '—' },
        {
          k: 'Fld',
          v: trunc(
            d.createFields
              .filter((f) => f.key.trim())
              .map((f) => `${f.key}`)
              .join(', ') || '—'
          ),
        },
      ];
    case 'delete-record':
      return [
        { k: 'Ent', v: d.deleteEntity || '—' },
        { k: 'Id', v: trunc(d.deleteRecordId || '—') },
      ];
    case 'logger':
      return [
        { k: 'Lvl', v: d.logLevel },
        { k: 'Msg', v: trunc(d.logMessage || '—') },
      ];
    case 'wait':
      if (d.waitDelayType === 'duration') {
        return [
          { k: '⏱', v: `${d.waitDuration || '0'} ${d.waitUnit}` },
          { k: '', v: 'then continue…' },
        ];
      }
      if (d.waitDelayType === 'until') {
        return [
          { k: 'Until', v: `${d.waitUntilTime} ${d.waitUntilTz}` },
          { k: 'Date', v: trunc(d.waitUntilDateLabel) },
        ];
      }
      return [
        { k: 'If', v: trunc(d.waitCondition || '—') },
        { k: 'Timeout', v: `${d.waitConditionTimeoutHours || '0'} h` },
      ];
    case 'condition-check':
      return [{ k: 'Cond', v: trunc(d.conditionExpr || '—', 52) }];
    case 'html-viewer':
      return [{ k: 'HTML', v: trunc((d.htmlViewerHtml ?? '').replace(/\s+/g, ' '), 48) }];
    default:
      return [{ k: '', v: '—' }];
  }
}

function stringsForKind(kind: ActionKindId, d: FlowActionPersistedData): string[] {
  const out: string[] = [];
  switch (kind) {
    case 'send-email':
      out.push(...d.emailToLines, ...d.emailCcLines, d.emailSubject, d.emailBody, ...d.emailAttachments);
      break;
    case 'send-slack':
      out.push(d.slackChannel, d.slackMessage, d.slackMention);
      break;
    case 'http-request':
      out.push(d.httpUrl, d.httpBody, ...d.httpHeaders.flatMap((h) => [h.key, h.value]));
      break;
    case 'update-crm':
      out.push(d.crmEntity, d.crmRecordId, ...d.crmFields.flatMap((f) => [f.key, f.value]));
      break;
    case 'emit-event':
      out.push(d.emitEventName, d.emitPayload);
      break;
    case 'create-record':
      out.push(d.createEntity, ...d.createFields.flatMap((f) => [f.key, f.value]));
      break;
    case 'delete-record':
      out.push(d.deleteEntity, d.deleteRecordId);
      break;
    case 'logger':
      out.push(d.logLevel, d.logMessage);
      break;
    case 'wait':
      out.push(
        d.waitDuration,
        d.waitUntilTime,
        d.waitUntilDateLabel,
        d.waitCondition,
        d.waitConditionTimeoutHours
      );
      break;
    case 'condition-check':
      out.push(d.conditionExpr);
      break;
    case 'html-viewer':
      out.push(d.htmlViewerHtml ?? '');
      break;
    default:
      break;
  }
  return out;
}

export function validateAction(d: FlowActionPersistedData): ActionValidation {
  const kind = d.actionKind;
  const missingRequired: string[] = [];
  const missingOptional: string[] = [];
  const strings = stringsForKind(kind, d);
  const varMappedCount = countVarsInStrings(strings);

  const nonEmpty = (s: string) => s.trim().length > 0;
  const anyTo = d.emailToLines.some(nonEmpty);

  switch (kind) {
    case 'send-email':
      if (!anyTo) missingRequired.push('to');
      if (!nonEmpty(d.emailSubject)) missingRequired.push('subject');
      if (!nonEmpty(d.emailBody)) missingOptional.push('body');
      break;
    case 'send-slack':
      if (!nonEmpty(d.slackChannel)) missingRequired.push('channel');
      if (!nonEmpty(d.slackMessage)) missingRequired.push('message');
      break;
    case 'http-request':
      if (!nonEmpty(d.httpUrl)) missingRequired.push('url');
      if (['POST', 'PUT', 'PATCH'].includes(d.httpMethod) && !nonEmpty(d.httpBody)) {
        missingOptional.push('body');
      }
      if (d.httpTimeoutMs > 30000) {
        missingOptional.push('timeout ≤ 30s');
      }
      break;
    case 'update-crm':
      if (!nonEmpty(d.crmEntity)) missingRequired.push('entity');
      if (!nonEmpty(d.crmRecordId)) missingRequired.push('recordId');
      if (!d.crmFields.some((f) => nonEmpty(f.key) && nonEmpty(f.value))) {
        missingRequired.push('fields');
      }
      break;
    case 'emit-event':
      if (!nonEmpty(d.emitEventName)) missingRequired.push('eventName');
      if (!nonEmpty(d.emitPayload)) missingOptional.push('payload');
      break;
    case 'create-record':
      if (!nonEmpty(d.createEntity)) missingRequired.push('entity');
      if (!d.createFields.some((f) => nonEmpty(f.key))) missingRequired.push('fields');
      break;
    case 'delete-record':
      if (!nonEmpty(d.deleteEntity)) missingRequired.push('entity');
      if (!nonEmpty(d.deleteRecordId)) missingRequired.push('recordId');
      break;
    case 'logger':
      if (!nonEmpty(d.logMessage)) missingRequired.push('message');
      break;
    case 'wait':
      if (d.waitDelayType === 'duration') {
        if (!nonEmpty(d.waitDuration)) missingRequired.push('duration');
      } else if (d.waitDelayType === 'condition') {
        if (!nonEmpty(d.waitCondition)) missingRequired.push('condition');
      } else {
        if (!nonEmpty(d.waitUntilTime)) missingOptional.push('time');
      }
      break;
    case 'condition-check':
      if (!nonEmpty(d.conditionExpr)) missingRequired.push('condition');
      break;
    case 'html-viewer':
      if (!nonEmpty(d.htmlViewerHtml ?? '')) missingOptional.push('html');
      break;
    default:
      break;
  }

  let level: ActionValidationLevel = 'valid';
  if (missingRequired.length > 0) level = 'error';
  else if (missingOptional.length > 0) level = 'warning';

  return { level, missingRequired, missingOptional, varMappedCount };
}

export function buildStandardActionOutputs(nodeName: string): string {
  return [
    `{{ ${nodeName}.success }}`,
    `{{ ${nodeName}.result }}`,
    `{{ ${nodeName}.error }}`,
    `{{ ${nodeName}.executedAt }}`,
  ].join('\n');
}

export type TriggerNodeDataWithSettings = TriggerNodeData & { settings?: TriggerSettings };

/**
 * Mustache templates a trigger exposes to downstream nodes (matches Trigger config “outputs” intent).
 * Manual: one line per defined variable `{{ trigger.variables.<name> }}` plus default output — no bogus `{{ manual.payload }}`.
 */
export function collectTriggerUpstreamTemplates(d: TriggerNodeDataWithSettings): string[] {
  const hints: string[] = [];
  const { triggerKind } = d;
  const settings = d.settings;

  if (triggerKind === 'manual') {
    if (settings?.kind === 'manual') {
      const seen = new Set<string>();
      for (const v of settings.variables) {
        const n = v.name?.trim();
        if (!n || seen.has(n)) continue;
        seen.add(n);
        hints.push(`{{ trigger.variables.${n} }}`);
      }
      if (hints.length === 0) {
        hints.push('{{ trigger.variables }}');
      }
      hints.push('{{ trigger.defaultoutput }}');
      return hints;
    }
    hints.push('{{ trigger.variables }}', '{{ trigger.timestamp }}', '{{ trigger.defaultoutput }}');
    return hints;
  }

  if (triggerKind === 'webhook' || triggerKind === 'event') {
    hints.push('{{ trigger.payload }}');
  }
  hints.push('{{ trigger.timestamp }}');
  return hints;
}

export function collectUpstreamVariableHints(
  nodeId: string,
  edges: { source: string; target: string; targetHandle?: string | null }[],
  nodes: Node[]
): string[] {
  const incoming = edges.filter((e) => e.target === nodeId && isFlowTargetHandle(e.targetHandle ?? undefined));
  const hints: string[] = [
    '{{ now }}',
    '{{ workflow.id }}',
    '{{ previous.output }}',
  ];

  for (const e of incoming) {
    const src = nodes.find((n) => n.id === e.source);
    if (!src) continue;
    if (src.type === 'triggerNode') {
      const d = src.data as TriggerNodeDataWithSettings;
      hints.push(...collectTriggerUpstreamTemplates(d));
    } else if (src.type === 'llmNode') {
      const d = src.data as FlowLlmNodePersistedData;
      hints.push(...buildLlmOutputLines(d.nodeName, d.mcpSteps, d.extraOutputKeys).split('\n').filter(Boolean));
    } else if (src.type === 'agentNode') {
      const d = src.data as FlowAgentPersistedData;
      hints.push(...buildAgentOutputLines(d.nodeName, d.outputSchemaJson).split('\n').filter(Boolean));
    } else if (src.type === 'actionNode') {
      const d = src.data as FlowActionPersistedData;
      hints.push(
        `{{ ${d.nodeName}.success }}`,
        `{{ ${d.nodeName}.result }}`,
        `{{ ${d.nodeName}.error }}`,
        `{{ ${d.nodeName}.executedAt }}`
      );
    } else if (src.type === 'flowFxNode') {
      hints.push(...flowFxOutputHints(src.data as FlowFxPersistedData));
    } else if (src.type === 'commonNode') {
      const d = src.data as NodeData;
      hints.push(`{{ ${slugifyAgentNodeName(d.label)}.output }}`);
    }
  }

  return Array.from(new Set(hints));
}

/**
 * Mustache templates for outputs exposed by immediate parent node(s) on the graph
 * (no global helpers like `{{ now }}`). Empty graph wiring falls back to `{{ previous.output }}`.
 */
export function collectParentConnectorOutputHints(
  nodeId: string,
  edges: { source: string; target: string; targetHandle?: string | null }[],
  nodes: Node[]
): string[] {
  const incoming = edges.filter((e) => e.target === nodeId && isFlowTargetHandle(e.targetHandle ?? undefined));
  const hints: string[] = [];
  for (const e of incoming) {
    const src = nodes.find((n) => n.id === e.source);
    if (!src) continue;
    if (src.type === 'triggerNode') {
      const d = src.data as TriggerNodeDataWithSettings;
      hints.push(...collectTriggerUpstreamTemplates(d));
    } else if (src.type === 'llmNode') {
      const d = src.data as FlowLlmNodePersistedData;
      hints.push(...buildLlmOutputLines(d.nodeName, d.mcpSteps, d.extraOutputKeys).split('\n').filter(Boolean));
    } else if (src.type === 'agentNode') {
      const d = src.data as FlowAgentPersistedData;
      hints.push(...buildAgentOutputLines(d.nodeName, d.outputSchemaJson).split('\n').filter(Boolean));
    } else if (src.type === 'actionNode') {
      const d = src.data as FlowActionPersistedData;
      hints.push(
        `{{ ${d.nodeName}.success }}`,
        `{{ ${d.nodeName}.result }}`,
        `{{ ${d.nodeName}.error }}`,
        `{{ ${d.nodeName}.executedAt }}`
      );
    } else if (src.type === 'flowFxNode') {
      hints.push(...flowFxOutputHints(src.data as FlowFxPersistedData));
    } else if (src.type === 'commonNode') {
      const d = src.data as NodeData;
      hints.push(`{{ ${slugifyAgentNodeName(d.label)}.output }}`);
    }
  }
  const out = Array.from(new Set(hints));
  return out.length > 0 ? out : ['{{ previous.output }}'];
}

export { newRowId };
